import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

const APP_NAME = "Farmacia Solidaria";

const clean = (value, fallback = "") => {
  if (value === null || value === undefined) return fallback;
  const s = String(value).trim();
  return s || fallback;
};

const fmtStatus = (status) => {
  const s = clean(status).toLowerCase();
  const map = {
    pending: "pendiente",
    "under_review": "en revisión",
    "en revision": "en revisión",
    approved: "aprobada",
    rejected: "rechazada",
    completed: "completada",
  };
  const canonical = map[s] || s;
  return canonical
    .split(" ")
    .map((p) => (p ? p[0].toUpperCase() + p.slice(1) : p))
    .join(" ");
};

const wrapHtml = (title, bodyHtml) => `
  <div style="font-family:Arial,Helvetica,sans-serif;line-height:1.5;color:#1f2937;max-width:640px;margin:0 auto;padding:16px;">
    <h2 style="margin:0 0 16px;">${title}</h2>
    <div style="font-size:15px;">${bodyHtml}</div>
    <hr style="margin:20px 0;border:none;border-top:1px solid #e5e7eb;" />
    <p style="margin:0;color:#6b7280;font-size:12px;">${APP_NAME} · Mensaje automático</p>
  </div>
`;

const enqueueEmail = async ({ to, subject, text, html, meta = {} }) => {
  const target = clean(to);
  if (!target) return;

  try {
    await addDoc(collection(db, "mail"), {
      to: [target],
      message: {
        subject: clean(subject, APP_NAME),
        text: clean(text),
        html: clean(html),
      },
      meta,
      createdAt: serverTimestamp(),
    });
  } catch (err) {
    console.error("No se pudo encolar email:", err);
  }
};

export const sendWelcomeEmail = async ({ to, name, role }) => {
  const roleTxt = clean(role, "usuario");
  const person = clean(name, "hola");

  const subject = `Bienvenido/a a ${APP_NAME}`;
  const text = `Hola ${person}. Tu cuenta fue creada correctamente con rol ${roleTxt}. Ya podés empezar a usar la plataforma.`;
  const html = wrapHtml(
    `Bienvenido/a a ${APP_NAME}`,
    `<p>Hola <strong>${person}</strong>,</p>
     <p>Tu cuenta fue creada correctamente con rol <strong>${roleTxt}</strong>.</p>
     <p>Ya podés publicar, solicitar y gestionar medicamentos desde la plataforma.</p>`
  );

  await enqueueEmail({
    to,
    subject,
    text,
    html,
    meta: { type: "welcome" },
  });
};

export const sendRequestCreatedEmails = async ({
  requestId,
  medicationName,
  requesterEmail,
  requesterName,
  donorEmail,
  donorName,
}) => {
  const med = clean(medicationName, "medicamento");
  const reqId = clean(requestId, "-");

  await enqueueEmail({
    to: requesterEmail,
    subject: `Solicitud creada: ${med}`,
    text: `Tu solicitud (${reqId}) para ${med} fue creada correctamente. Te avisaremos cuando el donante la revise.`,
    html: wrapHtml(
      "Solicitud registrada",
      `<p>Hola <strong>${clean(requesterName, "solicitante")}</strong>,</p>
       <p>Tu solicitud <strong>${reqId}</strong> para <strong>${med}</strong> se registró correctamente.</p>
       <p>Te avisaremos por este medio cuando haya novedades.</p>`
    ),
    meta: { type: "request_created", requestId: reqId },
  });

  await enqueueEmail({
    to: donorEmail,
    subject: `Nueva solicitud recibida: ${med}`,
    text: `Recibiste una nueva solicitud (${reqId}) para ${med}. Ingresá a la plataforma para aprobarla o rechazarla.`,
    html: wrapHtml(
      "Nueva solicitud recibida",
      `<p>Hola <strong>${clean(donorName, "donante")}</strong>,</p>
       <p>Recibiste una nueva solicitud <strong>${reqId}</strong> para <strong>${med}</strong>.</p>
       <p>Ingresá a la plataforma para aprobarla o rechazarla.</p>`
    ),
    meta: { type: "request_created_donor", requestId: reqId },
  });
};

export const sendRequestStatusEmails = async ({
  requestId,
  medicationName,
  status,
  requesterEmail,
  donorEmail,
  actorRole,
}) => {
  const reqId = clean(requestId, "-");
  const med = clean(medicationName, "medicamento");
  const statusTxt = fmtStatus(status);
  const actor = clean(actorRole, "sistema");

  const requesterSubject = `Actualización de solicitud ${reqId}: ${statusTxt}`;
  const requesterText = `Tu solicitud de ${med} cambió a estado ${statusTxt}. Acción realizada por ${actor}.`;
  const donorSubject = `Solicitud ${reqId} actualizada: ${statusTxt}`;
  const donorText = `La solicitud de ${med} cambió a estado ${statusTxt}. Acción realizada por ${actor}.`;

  await enqueueEmail({
    to: requesterEmail,
    subject: requesterSubject,
    text: requesterText,
    html: wrapHtml(
      "Solicitud actualizada",
      `<p>Tu solicitud <strong>${reqId}</strong> de <strong>${med}</strong> cambió a estado <strong>${statusTxt}</strong>.</p>
       <p>Acción realizada por: <strong>${actor}</strong>.</p>`
    ),
    meta: { type: "request_status", requestId: reqId, status: statusTxt, target: "requester" },
  });

  await enqueueEmail({
    to: donorEmail,
    subject: donorSubject,
    text: donorText,
    html: wrapHtml(
      "Solicitud actualizada",
      `<p>La solicitud <strong>${reqId}</strong> de <strong>${med}</strong> cambió a estado <strong>${statusTxt}</strong>.</p>
       <p>Acción realizada por: <strong>${actor}</strong>.</p>`
    ),
    meta: { type: "request_status", requestId: reqId, status: statusTxt, target: "donor" },
  });
};
